/** Justify layout for a single line */
export interface JustifyLineLayout {
  /** line.split(' ') — gap count = number of space characters */
  tokens: string[]
  /** Rendered width of each space slot when justified (0 when no gaps) */
  gap: number
  /** gap - natural space width — usable as CSS word-spacing */
  spacingDelta: number
}

/** Block-level measurements shared by the canvas renderer and the DOM overlay */
export interface TextBlockLayout {
  /** Widest line's natural advance width */
  maxWidth: number
  /** Natural width per line */
  lineWidths: number[]
  /** Per-line justify layout, parallel to the input lines */
  justify: JustifyLineLayout[]
}

/**
 * Compute intra-block alignment layout for multi-line text. The measure
 * function is injected so each caller (canvas ctx / DOM measuring ctx)
 * measures at its own font scale — keeping viewport preview and export
 * pixel-consistent.
 *
 * Justify: each line's inter-word spaces stretch so the line matches the
 * widest line. Gap count = number of space characters (consecutive spaces
 * produce empty tokens, one gap each — identical semantics to CSS
 * word-spacing). Lines without spaces fall back to left (gap 0).
 */
export const computeTextBlockLayout = (lines: string[], measure: (s: string) => number): TextBlockLayout => {
  const lineWidths = lines.map((line) => measure(line))
  const maxWidth = lineWidths.length > 0 ? Math.max(...lineWidths) : 0
  const spaceWidth = measure(' ')

  const justify = lines.map((line): JustifyLineLayout => {
    const tokens = line.split(' ')
    const gapCount = tokens.length - 1
    if (gapCount === 0) return {tokens, gap: 0, spacingDelta: 0}
    const sumWords = tokens.reduce((sum, token) => sum + measure(token), 0)
    const gap = (maxWidth - sumWords) / gapCount
    return {tokens, gap, spacingDelta: gap - spaceWidth}
  })

  return {maxWidth, lineWidths, justify}
}
